/**
 * Funções de validação reutilizáveis
 */

/**
 * Valida formato de email
 * @param {string} email - Email a ser validado
 * @returns {boolean}
 */
export function isValidEmail(email) {
    if (!email || typeof email !== 'string') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
}

/**
 * Valida CPF (formato básico - 11 dígitos)
 * @param {string} cpf - CPF a ser validado (pode estar mascarado)
 * @returns {boolean}
 */
export function isValidCPF(cpf) {
    if (!cpf || typeof cpf !== 'string') return false;
    const cleanCPF = cpf.replace(/\D/g, '');
    return cleanCPF.length === 11;
}

const CNPJ_WEIGHTS_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_WEIGHTS_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

function normalizeCnpjForValidation(cnpj) {
    const upper = String(cnpj ?? '').toUpperCase();
    let bare = '';
    for (const ch of upper) {
        if (bare.length >= 14) break;
        if (bare.length < 12) {
            if (/[A-Z0-9]/.test(ch)) bare += ch;
        } else if (/\d/.test(ch)) {
            bare += ch;
        }
    }
    return bare;
}

function cnpjCheckDigit(base, weights) {
    let sum = 0;
    for (let i = 0; i < base.length; i++) {
        sum += (base.charCodeAt(i) - 48) * weights[i];
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
}

/**
 * Valida CNPJ numérico tradicional ou alfanumérico (IN RFB 2.229/2024).
 * Aceita com ou sem máscara; verifica formato e dígitos verificadores (módulo 11 + ASCII−48).
 * @param {string} cnpj
 * @returns {boolean}
 */
export function isValidCNPJ(cnpj) {
    if (!cnpj || typeof cnpj !== 'string') return false;
    const clean = normalizeCnpjForValidation(cnpj);
    if (!/^[A-Z0-9]{12}\d{2}$/.test(clean)) return false;
    if (/^0{12}/.test(clean)) return false;
    const base = clean.slice(0, 12);
    const d1 = cnpjCheckDigit(base, CNPJ_WEIGHTS_DV1);
    const d2 = cnpjCheckDigit(base + String(d1), CNPJ_WEIGHTS_DV2);
    return clean.slice(12) === `${d1}${d2}`;
}

/**
 * Valida telefone brasileiro (formato básico - 10 ou 11 dígitos)
 * @param {string} phone - Telefone a ser validado (pode estar mascarado)
 * @returns {boolean}
 */
export function isValidPhone(phone) {
    if (!phone || typeof phone !== 'string') return false;
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 10 && cleanPhone.length <= 11;
}

/**
 * Valida CEP (formato básico - 8 dígitos)
 * @param {string} zipCode - CEP a ser validado (pode estar mascarado)
 * @returns {boolean}
 */
export function isValidZipCode(zipCode) {
    if (!zipCode || typeof zipCode !== 'string') return false;
    const cleanZipCode = zipCode.replace(/\D/g, '');
    return cleanZipCode.length === 8;
}

/**
 * Valida data no formato DD/MM/YYYY
 * @param {string} date - Data a ser validada
 * @returns {boolean}
 */
export function isValidDate(date) {
    if (!date || typeof date !== 'string') return false;
    if (date.length !== 10) return false;
    
    const parts = date.split('/');
    if (parts.length !== 3) return false;
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    
    if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
    if (day < 1 || day > 31) return false;
    if (month < 1 || month > 12) return false;
    if (year < 1900 || year > new Date().getFullYear()) return false;
    
    return true;
}

/**
 * Valida se um campo obrigatório está preenchido
 * @param {any} value - Valor a ser validado
 * @returns {boolean}
 */
export function isRequired(value) {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return true;
}

/**
 * Valida múltiplos campos obrigatórios
 * @param {Object} fields - Objeto com campos e valores
 * @returns {Object} Objeto com erros por campo
 */
export function validateRequiredFields(fields) {
    const errors = {};
    
    Object.entries(fields).forEach(([field, value]) => {
        if (!isRequired(value)) {
            errors[field] = `${field} é obrigatório`;
        }
    });
    
    return errors;
}

/**
 * Validação customizada com função de validação
 * @param {any} value - Valor a ser validado
 * @param {Function} validator - Função de validação
 * @param {string} errorMessage - Mensagem de erro caso falhe
 * @returns {string|null} Mensagem de erro ou null se válido
 */
export function validateWith(value, validator, errorMessage) {
    if (!validator(value)) {
        return errorMessage;
    }
    return null;
}
